import { useState, useEffect, useRef } from "react";
import { AiOutlineClose, AiOutlineScissor, AiOutlineSnippets } from "react-icons/ai";
import { HiBuildingOffice2, HiUserGroup, HiCog, HiCloud, HiServer, HiComputerDesktop } from "react-icons/hi2";
import { 
  FaChevronRight, FaChevronDown, FaPlus, FaFolderOpen, FaSearch, 
  FaHistory, FaTrash, FaEdit, FaInfoCircle, FaCheck 
} from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { MdBusiness, MdStore, MdFactory, MdLocalShipping, MdAccountBalance } from "react-icons/md";
import axios from "axios";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MoveHistoryPanel from "./MoveHistoryPanel";

const API_URL = `${process.env.REACT_APP_BE_MAIN_URL}/ocr/spaces/`;

// Visual Constants
const COLORS = {
  deepTeal: '#1e5045',
  cream: '#f6f4ed',
  orange: '#f26522',
  white: '#fff',
  accentGreen: '#94c43d',
  gray: '#e5e7eb',
  blue: '#3b82f6',
  red: '#ef4444',
  purple: '#8b5cf6',
  yellow: '#f59e0b',
  pink: '#ec4899',
  indigo: '#6366f1',
  teal: '#14b8a6'
};

const AVAILABLE_COLORS = [
  { name: 'Default', value: '' }, // Uses Deep Teal/Green defaults
  { name: 'Blue', value: COLORS.blue },
  { name: 'Red', value: COLORS.red },
  { name: 'Orange', value: COLORS.orange },
  { name: 'Purple', value: COLORS.purple },
  { name: 'Yellow', value: COLORS.yellow },
  { name: 'Pink', value: COLORS.pink },
  { name: 'Indigo', value: COLORS.indigo },
  { name: 'Teal', value: COLORS.teal },
];

const ICON_MAP = {
  'building': HiBuildingOffice2,
  'business': MdBusiness,
  'store': MdStore,
  'factory': MdFactory,
  'shipping': MdLocalShipping,
  'finance': MdAccountBalance,
  'group': HiUserGroup,
  'settings': HiCog,
  'cloud': HiCloud,
  'server': HiServer,
  'desktop': HiComputerDesktop,
};

const AVAILABLE_ICONS = Object.keys(ICON_MAP);

const buildTree = (spaces) => {
  const root = [];

  const getDepth = (item) => {
    let depth = 0;
    for (let i = 1; i <= 8; i++) {
        if (item[`level${i}`]) depth = i;
        else break;
    }
    return depth;
  };

  const sortedSpaces = [...spaces].sort((a, b) => getDepth(a) - getDepth(b));

  sortedSpaces.forEach((item) => {
    let currentLevel = root;
    let lastNode = null;
    const depth = getDepth(item);

    for (let i = 1; i <= 8; i++) {
      const key = `level${i}`;
      const name = item[key];
      if (!name) break;
      let existing = currentLevel.find((node) => node.name === name);
      if (!existing) {
        // Only the leaf node gets the DB ID, icon, color of this item
        // Intermediate nodes might be placeholders OR actual parents processed earlier
        const isTargetNode = (i === depth);
        const nodeId = isTargetNode ? `${item.id}-${i}` : `placeholder-${name}-${i}`;
        
        existing = { 
          id: nodeId, 
          name, 
          children: [], 
          folder_count: 0,
          created_by_name: isTargetNode ? item.created_by_name : null,
          created_at: isTargetNode ? item.created_at : null,
          icon: isTargetNode ? item.icon : null,
          color: isTargetNode ? item.color : null,
          dbId: isTargetNode ? item.id : null, 
          // If it's a placeholder initially, it might get updated later when the parent itself is processed
        };
        currentLevel.push(existing);
      } else if (i === depth) {
        // We found an existing node that matches the current item's level.
        // Update it with real data (it might have been a placeholder)
        if (existing.id.startsWith('placeholder-')) {
             existing.id = `${item.id}-${i}`;
        }
        existing.created_by_name = item.created_by_name;
        existing.created_at = item.created_at;
        existing.icon = item.icon;
        existing.color = item.color;
        existing.dbId = item.id;
      }
      lastNode = existing;
      currentLevel = existing.children;
    }
    if (lastNode && item.folder_count) {
      lastNode.folder_count = (lastNode.folder_count || 0) + item.folder_count;
    }
  });

  const sortTree = (nodes) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(node => node.children && sortTree(node.children));
  };
  sortTree(root);

  // Calculate cumulative descendant count for each node
  const countDescendants = (node) => {
    let count = node.children.length;
    node.children.forEach(child => {
      count += countDescendants(child);
    });
    node.descendant_count = count;
    return count;
  };
  root.forEach(node => countDescendants(node));

  return root;
};

export default function MyDataPage() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileSearchQuery, setFileSearchQuery] = useState("");
  const [fileSearchResults, setFileSearchResults] = useState([]);
  const [fileSearchProjects, setFileSearchProjects] = useState([]);
  const [fileSearchLoading, setFileSearchLoading] = useState(false);
  const [showFileSearchDropdown, setShowFileSearchDropdown] = useState(false);
  const fileSearchRef = useRef(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  
  // Modals & Panels
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createParentPath, setCreateParentPath] = useState([]);
  const [createName, setCreateName] = useState("");
  const [createIcon, setCreateIcon] = useState("");
  const [createColor, setCreateColor] = useState("");

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameData, setRenameData] = useState(null); // { id, name, icon, color }
  
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [selectedPropertyNode, setSelectedPropertyNode] = useState(null);

  // Context Menu & Clipboard
  const [contextMenu, setContextMenu] = useState(null); // { x, y, node, path }
  const [clipboard, setClipboard] = useState(null); // { id, name, mode: 'cut' }

  const history = useHistory();
  const menuRef = useRef(null);
  const token = sessionStorage.getItem("userToken");
  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

  // Map path strings to DB IDs for move operations
  const pathToDbIdRef = useRef({});

  useEffect(() => { fetchSpaces(); }, []);

  // Org-wide file search: debounced API call
  useEffect(() => {
    if (!fileSearchQuery || fileSearchQuery.length < 2) {
      setFileSearchResults([])
      setFileSearchProjects([])
      return
    }
    setFileSearchLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/ocr/search-files/?q=${encodeURIComponent(fileSearchQuery)}&limit=15`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setFileSearchResults(res.data.results || [])
        setFileSearchProjects(res.data.projects || [])
      } catch (err) {
        console.error('File search failed:', err)
        setFileSearchResults([])
        setFileSearchProjects([])
      } finally {
        setFileSearchLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [fileSearchQuery])

  // Close file search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fileSearchRef.current && !fileSearchRef.current.contains(e.target)) {
        setShowFileSearchDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
            setContextMenu(null);
        }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const buildPathToDbIdMap = (nodes, path = []) => {
    const map = {};
    nodes.forEach(node => {
      const nodePath = [...path, node.name];
      if (node.dbId) {
          map[nodePath.join('/')] = node.dbId;
      }
      if (node.children?.length > 0) {
        Object.assign(map, buildPathToDbIdMap(node.children, nodePath));
      }
    });
    return map;
  };

  const fetchSpaces = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${sessionStorage.getItem("userToken")}` }
      });
      const tree = buildTree(res.data);
      setSpaces(tree);
      pathToDbIdRef.current = buildPathToDbIdMap(tree);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Unable to load organization structure");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToProjects = (spacePath, autoOpenUploader = false) => {
    const selectedSpace = spacePath[spacePath.length - 1];
    sessionStorage.setItem("space", selectedSpace);
    history.push("/createfolder", {
      state: { list: spacePath, space: selectedSpace, username: sessionStorage.getItem("email"), space_path: spacePath.join("/"), autoOpenUploader }
    });
  };

  const toggleCollapse = (nodeId) => {
    setCollapsedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  // --- CRUD ACTIONS ---

  const handleCreate = async () => {
    if (!createName.trim()) { toast.warning("Please enter a valid space name."); return; }
    setLoading(true);
    try {
      const payload = { 
          level1: "", level2: "", level3: "", level4: "", level5: "", level6: "", level7: "", level8: "",
          icon: createIcon,
          color: createColor
      };
      createParentPath.forEach((name, i) => { payload[`level${i + 1}`] = name; });
      payload[`level${createParentPath.length + 1}`] = createName.trim();
      
      await axios.post(API_URL, payload, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${sessionStorage.getItem("userToken")}` }
      });
      toast.success("Space created successfully!");
      setCreateName(""); setCreateIcon(""); setCreateColor(""); setShowCreateModal(false);
      fetchSpaces();
    } catch (err) {
      console.error("Error creating space:", err.response?.data || err);
      toast.error("Failed to create space");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSpace = async (spaceDbId, spaceName) => {
    if(!window.confirm(`Are you sure you want to delete "${spaceName}"?`)) return;
    try {
      setLoading(true);
      await axios.delete(`${process.env.REACT_APP_BE_MAIN_URL}/ocr/spaces/${spaceDbId}/`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("userToken")}` }
      });
      toast.success(`Space "${spaceName}" deleted successfully!`);
      // check if deleted node was in properties panel
      if (selectedPropertyNode && String(selectedPropertyNode.dbId) === String(spaceDbId)) {
        setShowPropertiesPanel(false);
      }
      fetchSpaces();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.error || "Failed to delete space");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSpace = async () => {
    if (!renameData || !renameData.name.trim()) return;
    try {
      setLoading(true);
      // We'll use the rename endpoint, but we should also update icon/color.
      // Assuming a generic update endpoint exists or 'rename' handles partial updates if invalid
      // Actually, standard DRF ViewSet supports PATCH. 
      // The current Views seem to separate "Move" and "Rename".
      // Let's assume we need to implement a PATCH for details or use the Rename endpoint if that's all we have.
      // Based on previous code, there is a `SpacePathSerializer` with partial=True in POST.
      // But typically update is PUT/PATCH /ocr/spaces/<id>/.
      // IMPORTANT: The views.py read earlier didn't explicitly show a generic UpdateAPIView, 
      // but `SpacePathListCreateAPIView` is for list/create.
      // If there is no specific UpdateView, we might only be able to Rename.
      // Let's check `urls.py` later if needed. For now, we use the `rename` logic or assume PATCH works if standard ViewSet used.
      // Wait, earlier read of views.py showed `MoveSpaceView`. 
      // I will assume for now we use the /rename/ endpoint which was used in the previous file version.
      // Actually, looking at the previous file: axios.post(.../rename/...) was used.
      // I'll try to stick to that, or try PATCH if available. 
      // To be safe, let's assume I can only rename for now, unless I see an Update view.
      // Actually, I can use PATCH on the detail endpoint if `RetrieveUpdateDestroyAPIView` is used.
      // If not, I'll fallback to just rename.
      
      // Let's try PATCH to the detail ID.
      await axios.patch(`${API_URL}${renameData.id}/`, { 
        // We can't easily change levels directly without move, but we can change meta
        // But wait, the levels define the structure.
        // Changing name essentially changes levelX.
        // The backend `SpacePathSerializer` includes `icon` and `color`.
        // So a PATCH to /spaces/<id>/ should update fields that are editable.
        icon: renameData.icon,
        color: renameData.color
        // Name change is complex because it affects the path/levels. 
        // If the backend doesn't support renaming via PATCH levelX, we must use the custom rename endpoint.
      }, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("userToken")}` }
      });
      
      // If name changed, we might need the specific rename endpoint if PATCH doesn't handle hierarchy logic
      if (renameData.name !== renameData.originalName) {
           await axios.post(
            `${process.env.REACT_APP_BE_MAIN_URL}/ocr/spaces/${renameData.id}/rename/`,
            { new_name: renameData.name.trim() },
            { headers: { Authorization: `Bearer ${sessionStorage.getItem("userToken")}` } }
          );
      }

      toast.success("Space updated");
      setShowRenameModal(false);
      fetchSpaces();
    } catch (err) {
      console.error("Update error:", err);
      // Fallback for name change if PATCH failed (likely due to method allowed?)
      // We will refine this if we get 405.
      toast.error("Failed to update space details");
    } finally {
      setLoading(false);
    }
  };

  // --- MOVE / PASTE ---

  const handlePaste = async (targetNode) => {
    if (!clipboard || !clipboard.id) return;
    
    // Check if pasting into itself or children (basic check, backend does full check)
    if (clipboard.id === targetNode.dbId) {
        toast.warning("Cannot paste a space into itself.");
        return;
    }

    try {
        setLoading(true);
        // target_parent_id: null if root, else ID
        const targetId = targetNode ? parseInt(targetNode.dbId) : null;
        
        await axios.post(
            `${process.env.REACT_APP_BE_MAIN_URL}/ocr/spaces/${clipboard.id}/move/`,
            { target_parent_id: targetId },
            { headers: { Authorization: `Bearer ${sessionStorage.getItem("userToken")}` } }
        );
        toast.success(`Moved "${clipboard.name}" successfully.`);
        setClipboard(null);
        fetchSpaces();
    } catch (err) {
        console.error("Paste error:", err);
        toast.error(err.response?.data?.error || "Failed to move space");
    } finally {
        setLoading(false);
    }
  };

  // --- RENDER HELPERS ---

  const getIconComponent = (iconName) => {
      if (!iconName) return HiBuildingOffice2;
      return ICON_MAP[iconName] || HiBuildingOffice2;
  };

  // --- COMPONENT: ORG NODE ---
  
  const OrgNode = ({ space, path = [] }) => {
    const nodePath = [...path, space.name];
    const hasChildren = space.children?.length > 0;
    const isCollapsed = collapsedNodes.has(space.id);
    const IconCmp = getIconComponent(space.icon);
    
    // Styling
    // const borderColor = space.color || 'transparent'; // Unused
    const iconColor = space.color ? space.color : COLORS.deepTeal;
    const isCut = clipboard && clipboard.id === space.dbId;
    const canPaste = clipboard && clipboard.mode === 'cut' && clipboard.id !== space.dbId;

    const onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent bubbling to parent context menu
        setContextMenu({
            x: e.pageX,
            y: e.pageY,
            node: space,
            path: nodePath,
            dbId: space.dbId
        });
    };

    return (
      <div className="relative pl-6">
         {/* Connector Lines */}
         {/* Vertical line from parent to this node */}
         <div className="absolute top-0 left-0 w-px h-full bg-gray-300" style={{ left: 0, top: -12, height: 'calc(100% + 12px)' }}></div>
         <div className="absolute top-6 left-0 w-6 h-px bg-gray-300"></div>

        <div className="flex flex-col">
            <div 
                className={`group relative flex items-center p-2 bg-white rounded-lg border-l-4 shadow-sm hover:shadow-md transition-all cursor-context-menu ${isCut ? 'opacity-50 border-dashed' : ''}`}
                style={{ 
                    borderLeftColor: space.color || COLORS.deepTeal,
                    minWidth: '220px',
                    maxWidth: '300px' 
                }}
                onContextMenu={onContextMenu}
            >
                {/* Expander */}
                <div onClick={(e) => { e.stopPropagation(); toggleCollapse(space.id); }} className="p-1 mr-1 cursor-pointer hover:bg-gray-100 rounded">
                    {hasChildren ? (
                         isCollapsed ? <FaChevronRight className="text-gray-400 w-3 h-3"/> : <FaChevronDown className="text-gray-400 w-3 h-3"/>
                    ) : ( 
                        <div className="w-3 h-3"></div> 
                    )}
                </div>

                {/* Icon */}
                <div className={`p-2 rounded-lg mr-3`} style={{ backgroundColor: space.color ? `${space.color}15` : `${COLORS.deepTeal}15` }}>
                    <IconCmp className="w-5 h-5" style={{ color: iconColor }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" onClick={() => handleNavigateToProjects(nodePath)}>
                    <h4 className="font-medium text-gray-800 text-sm truncate select-none">{space.name}</h4>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5 select-none">
                        {space.folder_count > 0 && 
                            <span className="flex items-center gap-1"><FaFolderOpen className="w-2.5 h-2.5" /> {space.folder_count}</span>
                        }
                        {space.descendant_count > 0 && <span>{space.descendant_count} sub</span>}
                    </div>
                </div>

                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity mr-1 gap-1">
                    {/* Cut Button */}
                    <button
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            setClipboard({ id: space.dbId, name: space.name, mode: 'cut' }); 
                            toast.info(`Select destination to move "${space.name}"`); 
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-orange-500"
                        title="Move (Cut)"
                    >
                        <AiOutlineScissor />
                    </button>

                    {/* Paste Button */}
                    {canPaste && (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePaste(space);
                            }}
                            className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                            title={`Move "${clipboard.name}" here`}
                        >
                            <AiOutlineSnippets />
                        </button>
                    )}

                    {/* Quick Info Button */}
                    <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedPropertyNode(space); setShowPropertiesPanel(true); }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 transition-opacity"
                    >
                        <FaInfoCircle />
                    </button>
                </div>
            </div>

            {/* Children */}
            {hasChildren && !isCollapsed && (
                <div className="mt-2 relative space-y-2 border-l border-gray-300 ml-6 pl-0" style={{ borderLeft: 'none' }}>
                    {space.children.map(child => (
                        <OrgNode key={child.id} space={child} path={nodePath} />
                    ))}
                </div>
            )}
        </div>
      </div>
    );
  };


  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: COLORS.cream }}>
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] flex items-center justify-center z-[60]">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent"/>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shadow-sm z-10">
            <div>
                <h1 className="text-xl font-bold" style={{ color: COLORS.deepTeal }}>Organization Structure</h1>
                <button 
                  onClick={() => setShowHelpModal(true)} 
                  className="text-xs text-gray-500 hover:text-teal-600 flex items-center gap-1 mt-1 transition-colors group"
                >
                  <FaInfoCircle className="w-3 h-3 group-hover:text-teal-600" /> 
                  <span>Click for navigation & usage guide</span>
                </button>
            </div>
            <div className="flex items-center gap-3">
                 {/* File Search */}
                 <div className="relative" ref={fileSearchRef}>
                    <div className="flex items-center border rounded-lg px-2 py-1 gap-2">
                        <FaSearch className="w-3 h-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search drawings..."
                            value={fileSearchQuery}
                            onChange={(e) => {
                                setFileSearchQuery(e.target.value)
                                setShowFileSearchDropdown(true)
                            }}
                            onFocus={() => fileSearchQuery && setShowFileSearchDropdown(true)}
                            className="text-sm focus:outline-none w-52"
                        />
                        {fileSearchQuery && (
                            <button
                                onClick={() => {
                                    setFileSearchQuery('')
                                    setShowFileSearchDropdown(false)
                                    setFileSearchResults([])
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Clear search"
                            >
                                <FaXmark className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Search Results Dropdown */}
                    {showFileSearchDropdown && fileSearchQuery && fileSearchQuery.length >= 2 && (
                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50 w-96">
                            {fileSearchLoading && (
                                <div className="px-3 py-2 text-sm text-gray-500 italic">Searching...</div>
                            )}
                            {!fileSearchLoading && fileSearchResults.length === 0 && fileSearchProjects.length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-400 italic">No results found</div>
                            )}

                            {/* Matching Projects */}
                            {fileSearchProjects.length > 0 && (
                                <>
                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b">Projects</div>
                                    {fileSearchProjects.map((proj) => (
                                        <button
                                            key={`proj-${proj.id}`}
                                            onClick={() => {
                                                setShowFileSearchDropdown(false)
                                                setFileSearchQuery('')
                                                history.push('/workspace', {
                                                    state: {
                                                        folder_name: proj.folder_name,
                                                        space_path: proj.space_path,
                                                        username: sessionStorage.getItem("email"),
                                                        initialSearch: fileSearchQuery
                                                    }
                                                })
                                            }}
                                            className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                        >
                                            <div className="flex items-center gap-2">
                                                <FaFolderOpen className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                                                <span className="text-sm font-medium text-gray-900">{proj.folder_name}</span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5 ml-5 truncate">{proj.space_path}</div>
                                        </button>
                                    ))}
                                </>
                            )}

                            {/* Matching Files */}
                            {fileSearchResults.length > 0 && (
                                <>
                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b">Drawings</div>
                                    {fileSearchResults.map((result) => (
                                        <button
                                            key={result._id}
                                            onClick={() => {
                                                setShowFileSearchDropdown(false)
                                                setFileSearchQuery('')
                                                history.push('/workspace', {
                                                    state: {
                                                        folder_name: result.folder_name,
                                                        space_path: result.space_path,
                                                        username: sessionStorage.getItem("email"),
                                                        initialSearch: result.filename
                                                    }
                                                })
                                            }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-green-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                        >
                                            <div className="text-sm font-medium text-gray-900 truncate">{result.filename}</div>
                                            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                                                <span className="truncate">{result.space_path}</span>
                                                <span className="text-gray-300">/</span>
                                                <span className="font-medium text-gray-600">{result.folder_name}</span>
                                                {result.label && (
                                                    <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-purple-50 text-purple-600 rounded">{result.label}</span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                 </div>

                 {clipboard && (
                    <div className="flex items-center gap-2 bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200 text-sm text-orange-800 mr-2 animate-in fade-in zoom-in-95">
                        <span className="font-medium">Moving: {clipboard.name}</span>
                        <button onClick={() => setClipboard(null)} className="hover:bg-orange-200 rounded p-0.5 text-orange-600" title="Cancel Move"><AiOutlineClose /></button>
                    </div>
                 )}

                 <button onClick={() => setShowHistoryPanel(true)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600" title="History">
                    <FaHistory />
                 </button>
            </div>
          </header>

          {/* Org Chart Canvas */}
          <div className="flex-1 overflow-auto p-8 relative" onContextMenu={(e) => {
               // Context menu for the background (Paste at root)
                e.preventDefault();
                setContextMenu({ x: e.pageX, y: e.pageY, node: null }); 
          }}>
              <div className="min-w-max pb-20">
                    {/* Root Nodes */}
                    <div className="space-y-4">
                        {spaces.map(rootNode => (
                            <OrgNode key={rootNode.id} space={rootNode} />
                        ))}
                    </div>
                    
                    {/* Empty State */}
                    {spaces.length === 0 && !loading && (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                             <HiBuildingOffice2 className="w-16 h-16 mb-4 opacity-20" />
                             <p>Organization is empty</p>
                             <button 
                                onClick={() => { setCreateParentPath([]); setCreateIcon(''); setCreateColor(''); setShowCreateModal(true); }}
                                className="mt-4 px-4 py-2 bg-teal-700 text-white rounded shadow hover:bg-teal-800"
                             >
                                Create Root Space
                             </button>
                        </div>
                    )}
              </div>
          </div>
      </div>

      {/* Right Sidebar: Properties */}
      {showPropertiesPanel && selectedPropertyNode && (
          <div className="w-80 bg-white border-l border-gray-200 shadow-xl flex flex-col z-20">
             <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <span className="font-semibold text-gray-700">Space Details</span>
                <button onClick={() => setShowPropertiesPanel(false)} className="text-gray-400 hover:text-gray-600">
                    <AiOutlineClose />
                </button>
             </div>
             <div className="p-6 flex-1 overflow-auto">
                 <div className="flex justify-center mb-6">
                     <div 
                        className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-inner"
                        style={{ backgroundColor: selectedPropertyNode.color ? `${selectedPropertyNode.color}20` : `${COLORS.deepTeal}10` }}
                     > 
                        {(() => {
                            const Icon = getIconComponent(selectedPropertyNode.icon);
                            return <Icon className="w-10 h-10" style={{ color: selectedPropertyNode.color || COLORS.deepTeal }} />;
                        })()}
                     </div>
                 </div>

                 <div className="space-y-6">
                     <div>
                        <label className="text-xs uppercase font-bold text-gray-400 tracking-wider">Name</label>
                        <p className="text-lg font-medium text-gray-800">{selectedPropertyNode.name}</p>
                     </div>
                     
                     <div className="grid grid-cols-2 gap-4">
                         <div className="bg-gray-50 p-3 rounded-lg">
                            <label className="text-xs text-gray-500">Created By</label>
                            <p className="font-medium text-sm text-gray-700">{selectedPropertyNode.created_by_name || 'System'}</p>
                         </div>
                         <div className="bg-gray-50 p-3 rounded-lg">
                            <label className="text-xs text-gray-500">Created At</label>
                            <p className="font-medium text-sm text-gray-700">
                                {selectedPropertyNode.created_at ? new Date(selectedPropertyNode.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                         </div>
                     </div>

                     <div className="space-y-2">
                        <label className="text-xs uppercase font-bold text-gray-400 tracking-wider">Storage Stats</label>
                        <div className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                             <div className="flex items-center gap-3">
                                 <div className="p-2 bg-blue-50 rounded text-blue-500"><FaFolderOpen /></div>
                                 <span className="text-sm font-medium">Projects</span>
                             </div>
                             <span className="text-lg font-semibold text-gray-700">{selectedPropertyNode.folder_count || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                             <div className="flex items-center gap-3">
                                 <div className="p-2 bg-purple-50 rounded text-purple-500"><HiServer /></div>
                                 <span className="text-sm font-medium">Sub-spaces</span>
                             </div>
                             <span className="text-lg font-semibold text-gray-700">{selectedPropertyNode.children?.length || 0}</span>
                        </div>
                     </div>
                 </div>
             </div>
             <div className="p-4 border-t border-gray-200 bg-gray-50">
                 <button 
                    onClick={() => handleNavigateToProjects([ ...createParentPath, selectedPropertyNode.name])} // Approximation
                    className="w-full py-2 bg-white border border-gray-300 rounded shadow-sm text-gray-700 font-medium hover:bg-gray-50"
                 >
                    Manage Projects
                 </button>
             </div>
          </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
          <div 
             ref={menuRef}
             className="absolute bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[180px] z-50 animate-in fade-in zoom-in-95 duration-100"
             style={{ top: Math.min(contextMenu.y, window.innerHeight - 250), left: Math.min(contextMenu.x, window.innerWidth - 200) }}
          >
              {contextMenu.node ? (
                  <>
                    <div className="px-3 py-2 border-b border-gray-50 mb-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate max-w-[150px]">{contextMenu.node.name}</p>
                    </div>

                    <button onClick={() => { handleNavigateToProjects(contextMenu.path, true); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                        <FaFolderOpen className="text-blue-400" /> Create New Project
                    </button>
                    
                    <button onClick={() => { setCreateParentPath(contextMenu.path); setCreateIcon(''); setCreateColor(''); setShowCreateModal(true); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                        <FaPlus className="text-gray-400" /> Add Sub-space
                    </button>
                    
                    <button onClick={() => { setRenameData({ id: contextMenu.dbId, name: contextMenu.node.name, originalName: contextMenu.node.name, icon: contextMenu.node.icon, color: contextMenu.node.color }); setShowRenameModal(true); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                        <FaEdit className="text-gray-400" /> Edit Details
                    </button>
                    
                    <div className="my-1 border-t border-gray-100"></div>

                    <button onClick={() => { setClipboard({ id: contextMenu.dbId, name: contextMenu.node.name, mode: 'cut' }); setContextMenu(null); toast.info(`Cut "${contextMenu.node.name}" to clipboard`); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                        <AiOutlineScissor className="text-gray-400" /> Cut
                    </button>
                    
                    {clipboard && clipboard.mode === 'cut' && clipboard.id !== contextMenu.dbId && (
                        <button onClick={() => { handlePaste(contextMenu.node); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700 font-medium">
                            <AiOutlineSnippets className="text-teal-600" /> Paste "{clipboard.name}"
                        </button>
                    )}

                    <div className="my-1 border-t border-gray-100"></div>
                    
                    <button onClick={() => { handleDeleteSpace(contextMenu.dbId, contextMenu.node.name); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-red-50 text-sm flex items-center gap-2 text-red-600">
                        <FaTrash className="opacity-70" /> Delete
                    </button>
                  </>
              ) : (
                  <>
                     <button onClick={() => { setCreateParentPath([]); setCreateIcon(''); setCreateColor(''); setShowCreateModal(true); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                        <FaPlus className="text-gray-400" /> New Root Space
                    </button>
                     {clipboard && (
                        <button onClick={() => { handlePaste(null); setContextMenu(null); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700 font-medium">
                            <AiOutlineSnippets className="text-teal-600" /> Paste "{clipboard.name}" at Root
                        </button>
                    )}
                  </>
              )}
          </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                 <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Create New Space</h3>
                    <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600"><AiOutlineClose /></button>
                 </div>
                 <div className="p-6 space-y-4">
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Parent Location</label>
                         <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded truncate">
                             {createParentPath.length ? createParentPath.join(' / ') : 'Root (Top Level)'}
                         </div>
                     </div>
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Space Name</label>
                         <input 
                            type="text" autoFocus 
                            value={createName} onChange={e => setCreateName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-shadow"
                            placeholder="e.g. Finance, Project Alpha..."
                         />
                     </div>
                     
                     {/* Icon Selection */}
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                         <div className="grid grid-cols-6 gap-2">
                             {AVAILABLE_ICONS.map(iconKey => {
                                 const Icon = getIconComponent(iconKey);
                                 const isSelected = createIcon === iconKey;
                                 return (
                                     <button 
                                        key={iconKey}
                                        onClick={() => setCreateIcon(iconKey)}
                                        className={`p-2 rounded-lg flex items-center justify-center transition-all ${isSelected ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                                     >
                                        <Icon className="w-5 h-5" />
                                     </button>
                                 )
                             })}
                         </div>
                     </div>

                     {/* Color Selection */}
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">Color Tag</label>
                         <div className="flex flex-wrap gap-2">
                             {AVAILABLE_COLORS.map(color => {
                                 const isSelected = createColor === color.value;
                                 return (
                                     <button 
                                        key={color.name}
                                        onClick={() => setCreateColor(color.value)}
                                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform ${isSelected ? 'scale-110 border-gray-600' : 'border-transparent hover:scale-105'}`}
                                        style={{ backgroundColor: color.value || COLORS.deepTeal }}
                                        title={color.name}
                                     >
                                         {isSelected && <FaCheck className="text-white w-3 h-3 drop-shadow-md" />}
                                     </button>
                                 )
                             })}
                         </div>
                     </div>

                 </div>
                 <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                     <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
                     <button onClick={handleCreate} className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 shadow-sm">Create Space</button>
                 </div>
             </div>
          </div>
      )}

      {/* Rename/Edit Modal */}
      {showRenameModal && renameData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                 <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Edit Space</h3>
                    <button onClick={() => setShowRenameModal(false)} className="text-gray-400 hover:text-gray-600"><AiOutlineClose /></button>
                 </div>
                 <div className="p-6 space-y-4">
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Space Name</label>
                         <input 
                            type="text" autoFocus 
                            value={renameData.name} onChange={e => setRenameData({...renameData, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-shadow"
                         />
                     </div>

                      {/* Icon Selection */}
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                         <div className="grid grid-cols-6 gap-2">
                             {AVAILABLE_ICONS.map(iconKey => {
                                 const Icon = getIconComponent(iconKey);
                                 const isSelected = renameData.icon === iconKey;
                                 return (
                                     <button 
                                        key={iconKey}
                                        onClick={() => setRenameData({...renameData, icon: iconKey})}
                                        className={`p-2 rounded-lg flex items-center justify-center transition-all ${isSelected ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                                     >
                                        <Icon className="w-5 h-5" />
                                     </button>
                                 )
                             })}
                         </div>
                     </div>

                     {/* Color Selection */}
                     <div>
                         <label className="block text-sm font-medium text-gray-700 mb-2">Color Tag</label>
                         <div className="flex flex-wrap gap-2">
                             {AVAILABLE_COLORS.map(color => {
                                 const isSelected = renameData.color === color.value;
                                 return (
                                     <button 
                                        key={color.name}
                                        onClick={() => setRenameData({...renameData, color: color.value})}
                                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform ${isSelected ? 'scale-110 border-gray-600' : 'border-transparent hover:scale-105'}`}
                                        style={{ backgroundColor: color.value || COLORS.deepTeal }}
                                        title={color.name}
                                     >
                                         {isSelected && <FaCheck className="text-white w-3 h-3 drop-shadow-md" />}
                                     </button>
                                 )
                             })}
                         </div>
                     </div>

                 </div>
                 <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                     <button onClick={() => setShowRenameModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
                     <button onClick={handleUpdateSpace} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm">Save Changes</button>
                 </div>
             </div>
          </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
             <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                 <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <FaInfoCircle className="text-teal-600" /> 
                        Organizational Chart Guide
                    </h3>
                    <button onClick={() => setShowHelpModal(false)} className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 shadow-sm transition-all"><AiOutlineClose /></button>
                 </div>
                 <div className="p-8 overflow-y-auto">
                     <div className="grid md:grid-cols-2 gap-8">
                         <div className="space-y-6">
                             <div>
                                 <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><FaFolderOpen className="text-blue-500" /> Navigation</h4>
                                 <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside bg-gray-50 p-3 rounded-lg">
                                     <li>Click on a space name to <strong>view and manage its projects</strong>.</li>
                                     <li>Use the small <FaChevronRight className="inline text-gray-400 mx-1" /> arrows to expand or collapse branches.</li>
                                     <li>Use the search bar at the top to filter visible spaces.</li>
                                     <li>Right-click anywhere on the background to create a new <strong>Root Level Space</strong>.</li>
                                 </ul>
                             </div>
                             <div>
                                 <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><AiOutlineScissor className="text-orange-500" /> Moving Spaces</h4>
                                 <div className="text-sm text-gray-600 space-y-3 bg-gray-50 p-3 rounded-lg">
                                     <p>Spaces can be reorganized using the "Cut & Paste" method:</p>
                                     <ol className="list-decimal list-inside space-y-1 ml-1">
                                         <li>Hover over the space you want to move.</li>
                                         <li>Click the <strong>Scissor icon</strong> (Cut).</li>
                                         <li>Navigate to the new destination.</li>
                                         <li>Hover over the destination space and click the <strong>Paste icon</strong>.</li>
                                     </ol>
                                     <p className="text-xs text-gray-500 italic mt-2">You can also use the Right-Click menu to Cut spaces.</p>
                                 </div>
                             </div>
                         </div>
                         <div className="space-y-6">
                             <div>
                                 <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><HiCog className="text-gray-500" /> Management</h4>
                                 <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside bg-gray-50 p-3 rounded-lg">
                                     <li><strong>Right-click</strong> on any space to open the Management Menu.</li>
                                     <li>Select <strong>Add Sub-space</strong> to create a new child space.</li>
                                     <li>Select <strong>Edit Details</strong> to rename, change icon, or color.</li>
                                     <li>Select <strong>Create New Project</strong> to immediately start a project in that folder.</li>
                                     <li>Select <strong>Delete</strong> to remove a space (must be empty).</li>
                                 </ul>
                             </div>
                             <div>
                                 <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><FaInfoCircle className="text-teal-500" /> Properties</h4>
                                 <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                                     Click the small <strong>Info (i) icon</strong> on any card to slide out the Properties Panel. This shows creation details and storage statistics.
                                 </p>
                             </div>
                         </div>
                     </div>
                 </div>
                 <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                     <button onClick={() => setShowHelpModal(false)} className="px-6 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 shadow-sm transition-colors">Got it!</button>
                 </div>
             </div>
          </div>
      )}

      {showHistoryPanel && (
         <MoveHistoryPanel onClose={() => setShowHistoryPanel(false)} />
      )}
    </div>
  );
}
